/**
 * What the outside world says, kept visibly apart from what the legajo says.
 *
 * A figure from audited statements and a claim from a news site do not carry the same weight, so
 * they are never in the same table.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { enrichmentGroups } from "../../lib/model";
import { EmptyState, SectionTitle, Spinner, Table, Td, Th } from "../../components/ui";

export default function Sources({ caseId, runId }: { caseId: string; runId: string | null }) {
  const findings = useQuery({
    queryKey: ["enrichment", caseId, runId],
    queryFn: () => api.getEnrichment(caseId, runId),
  });

  if (findings.isLoading) return <Spinner />;
  if (!findings.data?.length) {
    return (
      <EmptyState
        title="Todavía no se consultaron fuentes externas"
        text="Al analizar el legajo se consulta la Central de Deudores del BCRA y una lista acotada de medios."
      />
    );
  }

  return (
    <>
      <p className="text-sm text-muted">
        Información de fuera del legajo. Cada hallazgo trae su fuente: se pondera distinto que un
        estado contable auditado.
      </p>

      {enrichmentGroups(findings.data).map((group) => (
        <div key={group.source}>
          <SectionTitle>{group.source}</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Hallazgo</Th>
                <Th>Detalle</Th>
                <Th>Ver fuente</Th>
              </tr>
            </thead>
            <tbody>
              {group.findings.map((finding) => (
                <tr key={finding.id}>
                  <Td className="font-medium">{finding.title}</Td>
                  <Td>{finding.summary}</Td>
                  <Td>
                    {finding.url ? (
                      <a
                        href={finding.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-primary underline"
                      >
                        Abrir
                      </a>
                    ) : (
                      <span className="text-muted">{finding.api_ref ?? "—"}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}
    </>
  );
}
